import { z } from 'zod';

export enum SupplyListsTab {
  All = 'all',
  Rewrite = 'rewrite',
  Schain = 'schain',
}

export enum SupplyListType {
  Rewrite = 'REWRITE',
  Schain = 'SCHAIN',
}

export enum RewriteOperator {
  Equals = 'eq',
  NotEquals = 'neq',
  GreaterThan = 'gt',
  GreaterThanOrEquals = 'gte',
  LessThan = 'lt',
  LessThanOrEquals = 'lte',
  Between = 'between',
  Exists = 'exists',
  NotExists = 'not_exists',
  Contains = 'contains',
  In = 'in',
}

export enum SchainType {
  Bundle = 'BUNDLE',
  Domain = 'DOMAIN',
}

export enum ListsSortDirection {
  Ascending = 'ASC',
  Descending = 'DESC',
}

export interface ListsFilter {
  searchTerm: string;
  page: number;
  size: number;
}

export interface ListsPage<TItem> {
  content: TItem[];
  totalElements: number;
}

export interface AllListsPage {
  rewrite: RewriteList[];
  schain: SchainList[];
  totalElements: number;
  partialFailure: boolean;
}

export const listsPageSizes = [5, 10, 25, 50, 100] as const;
export const defaultListsFilter: ListsFilter = { searchTerm: '', page: 0, size: 10 };

const entityIdSchema = z.union([z.string(), z.number()]).transform(String);
const nullableTextSchema = z.string().nullish();

export const rewriteListSchema = z.looseObject({
  id: entityIdSchema,
  name: z.string(),
  rules: z.string(),
  createdAt: nullableTextSchema,
  updatedAt: nullableTextSchema,
});

export const schainEntrySchema = z.looseObject({
  id: entityIdSchema.optional(),
  bundle: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  asi: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  sid: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  pid: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  ipd: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
});

export const schainListSchema = z.looseObject({
  id: entityIdSchema,
  name: z.string(),
  enableRotationForDuplicateBundles: z.boolean().nullish().transform(Boolean),
  type: z.enum(SchainType),
  schainBundleList: z
    .array(schainEntrySchema)
    .nullish()
    .transform((value) => value ?? []),
  createdAt: nullableTextSchema,
  updatedAt: nullableTextSchema,
});

export const rewriteListsPageSchema = z.looseObject({
  content: z.array(rewriteListSchema),
  totalElements: z.number(),
});

export const schainListsPageSchema = z.looseObject({
  content: z.array(schainListSchema),
  totalElements: z.number(),
});

export type RewriteList = z.infer<typeof rewriteListSchema>;
export type SchainEntry = z.infer<typeof schainEntrySchema>;
export type SchainList = z.infer<typeof schainListSchema>;

export interface RewriteListMutationPayload {
  name: string;
  rules: string;
}

export interface SchainListMutationPayload {
  name: string;
  enableRotationForDuplicateBundles: boolean;
  type: SchainType;
  schainBundleList: SchainEntry[];
}

export interface RewriteCondition {
  path: string;
  op: RewriteOperator;
  val?: unknown;
  val2?: unknown;
}

export interface RewriteSet {
  field: string;
  value?: unknown;
  overwrite: boolean;
  nullify: boolean;
}

export interface RewriteRule {
  condGroups: RewriteCondition[][];
  sets: RewriteSet[];
}

const rewriteConditionSchema = z.looseObject({
  path: z.string(),
  op: z.enum(RewriteOperator),
  val: z.unknown().optional(),
  val2: z.unknown().optional(),
});

const rewriteSetSchema = z
  .looseObject({
    field: z.string().optional(),
    value: z.unknown().optional(),
    path: z.string().optional(),
    val: z.unknown().optional(),
    overwrite: z.boolean().optional(),
    nullify: z.boolean().optional(),
  })
  .transform((set): RewriteSet => ({
    field: set.field ?? set.path ?? '',
    value: set.value ?? set.val,
    overwrite: Boolean(set.overwrite),
    nullify: Boolean(set.nullify),
  }));

const rewriteRuleSchema = z.looseObject({
  condGroups: z.array(z.array(rewriteConditionSchema)).default([]),
  sets: z.array(rewriteSetSchema),
});

export const rewriteRulesSchema = z.array(rewriteRuleSchema);

export function parseRewriteRules(rawRules: string) {
  try {
    return rewriteRulesSchema.safeParse(JSON.parse(rawRules));
  } catch {
    return rewriteRulesSchema.safeParse(undefined);
  }
}

export function stringifyRewriteValue(value: unknown) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

export function rewriteRuleSetCount(item: RewriteList) {
  const result = parseRewriteRules(item.rules);
  return result.success ? result.data.reduce((total, rule) => total + rule.sets.length, 0) : null;
}
